/**
 * Pure page-layout computation for the DOCX editor.
 *
 * This module has **no** TipTap / ProseMirror imports — it works
 * entirely with DOM measurements and a typed {@link PageConfig}.
 * The TipTap extension (`page-breaks.ts`) calls into this module
 * and translates the results into ProseMirror decorations.
 *
 * Layout rules mirror Google Docs / DOCX behaviour:
 *  - break when accumulated blocks exceed page content height
 *  - keep-with-next for headings
 *  - prefer keeping tables and images on a single page
 *  - avoid orphan/widow blocks at page boundaries
 *  - graceful handling of oversized elements
 */

import { type PageConfig, contentHeight } from "./page-config";

// ── Types ──────────────────────────────────────────────────────────

export interface BlockInfo {
  element: HTMLElement;
  /** Semantic node type derived from the DOM tag / class. */
  nodeType: BlockNodeType;
  /** Top edge relative to the content container (spacer-adjusted). */
  top: number;
  /** Bottom edge relative to the content container (spacer-adjusted). */
  bottom: number;
  headingLevel?: number;
}

export type BlockNodeType =
  | "heading"
  | "paragraph"
  | "table"
  | "image"
  | "list"
  | "blockquote"
  | "codeBlock"
  | "horizontalRule"
  | "other";

/** Index into the `BlockInfo[]` array indicating where to insert a page break. */
export interface BreakPosition {
  /** Break is inserted *before* this block index. */
  blockIndex: number;
  /** The content-relative y-coordinate where the new page starts. */
  pageTop: number;
}

// ── Block measurement ──────────────────────────────────────────────

const HEADING_RE = /^H([1-6])$/;

function classifyElement(el: HTMLElement): { type: BlockNodeType; headingLevel?: number } {
  const tag = el.tagName;

  const headingMatch = HEADING_RE.exec(tag);
  if (headingMatch) {
    return { type: "heading", headingLevel: Number(headingMatch[1]) };
  }

  if (tag === "TABLE" || el.classList.contains("tableWrapper") || el.classList.contains("docx-table")) {
    return { type: "table" };
  }
  if (tag === "IMG" || el.classList.contains("docx-image") || tag === "FIGURE") {
    return { type: "image" };
  }
  if (tag === "UL" || tag === "OL") return { type: "list" };
  if (tag === "BLOCKQUOTE") return { type: "blockquote" };
  if (tag === "PRE") return { type: "codeBlock" };
  if (tag === "HR") return { type: "horizontalRule" };
  if (tag === "P") return { type: "paragraph" };
  if (tag === "DIV" && el.classList.contains("docx-image-wrapper")) {
    return { type: "image" };
  }

  return { type: "other" };
}

/**
 * Walk the editor DOM and produce a measured {@link BlockInfo} for
 * every top-level content node, compensating for any existing
 * page-break spacer decorations already in the DOM.
 */
export function measureBlocks(container: HTMLElement): BlockInfo[] {
  const containerTop = container.getBoundingClientRect().top;
  const blocks: BlockInfo[] = [];
  let spacerOffset = 0;

  for (let i = 0; i < container.childNodes.length; i++) {
    const child = container.childNodes[i];
    if (!(child instanceof HTMLElement)) continue;

    if (child.hasAttribute("data-page-break")) {
      spacerOffset += child.getBoundingClientRect().height;
      continue;
    }

    const rect = child.getBoundingClientRect();
    if (rect.height === 0) continue;

    const { type, headingLevel } = classifyElement(child);

    blocks.push({
      element: child,
      nodeType: type,
      top: rect.top - containerTop - spacerOffset,
      bottom: rect.bottom - containerTop - spacerOffset,
      headingLevel,
    });
  }

  return blocks;
}

// ── Break computation ──────────────────────────────────────────────

/**
 * Compute page-break positions from measured blocks and semantic rules.
 *
 * Returns break positions as block indices — "insert a break before
 * `blocks[breakIndex]`".  The caller translates those into ProseMirror
 * widget decoration positions.
 */
export function computeBreaks(blocks: BlockInfo[], config: PageConfig): BreakPosition[] {
  const pageH = contentHeight(config);
  if (blocks.length === 0) return [];

  const raw = computeRawBreaks(blocks, pageH);
  const refined = applySemanticRules(raw, blocks, pageH);
  return deduplicateBreaks(refined);
}

// ── Raw geometry pass ──────────────────────────────────────────────

/**
 * Walk blocks linearly and insert a break whenever a block's bottom
 * edge exceeds the current page boundary.  The break is placed
 * *before* the offending block (pushing it to the next page) unless
 * the block itself is taller than a full page (oversized), in which
 * case we let it overflow and advance the page boundary past it.
 */
function computeRawBreaks(blocks: BlockInfo[], pageH: number): BreakPosition[] {
  const breaks: BreakPosition[] = [];
  let pageBottom = pageH;

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const blockH = b.bottom - b.top;

    if (b.bottom > pageBottom) {
      if (blockH <= pageH) {
        breaks.push({ blockIndex: i, pageTop: b.top });
        pageBottom = b.top + pageH;
      } else {
        // Oversized block: let it start where it is and advance past it.
        // If it doesn't start at the current page top, push it to a new page.
        const pageStart = pageBottom - pageH;
        if (b.top > pageStart + 1) {
          breaks.push({ blockIndex: i, pageTop: b.top });
          pageBottom = b.top + pageH;
        }
        while (b.bottom > pageBottom) {
          pageBottom += pageH;
        }
      }
    }
  }

  return breaks;
}

// ── Semantic rule application ──────────────────────────────────────

const WIDOW_THRESHOLD = 42;
const ORPHAN_THRESHOLD = 42;

function applySemanticRules(
  breaks: BreakPosition[],
  blocks: BlockInfo[],
  pageH: number,
): BreakPosition[] {
  let result = [...breaks];
  result = applyKeepHeadingWithNext(result, blocks);
  result = applyTableAwareBreak(result, blocks, pageH);
  result = applyImageAwareBreak(result, blocks, pageH);
  result = applyAvoidOrphanBlock(result, blocks);
  result = applyAvoidWidowBlock(result, blocks, pageH);
  return result;
}

/**
 * **Keep-with-next** — If the block immediately before a break is a
 * heading, move the break to before the heading so the heading starts
 * the new page together with its following content.
 */
function applyKeepHeadingWithNext(
  breaks: BreakPosition[],
  blocks: BlockInfo[],
): BreakPosition[] {
  return breaks.map((bp) => {
    const prev = bp.blockIndex - 1;
    if (prev >= 0 && blocks[prev].nodeType === "heading") {
      return { blockIndex: prev, pageTop: blocks[prev].top };
    }
    return bp;
  });
}

/**
 * **Table-aware** — If a table fits on a full page but crosses the
 * current page boundary, push it to the next page.
 */
function applyTableAwareBreak(
  breaks: BreakPosition[],
  blocks: BlockInfo[],
  pageH: number,
): BreakPosition[] {
  return breaks.map((bp) => {
    const idx = bp.blockIndex;
    if (idx <= 0) return bp;

    const target = blocks[idx];
    const targetH = target.bottom - target.top;
    if (target.nodeType === "table" && targetH <= pageH) {
      return bp;
    }

    const prev = blocks[idx - 1];
    const prevH = prev.bottom - prev.top;
    if (prev.nodeType === "table" && prevH <= pageH) {
      return { blockIndex: idx - 1, pageTop: prev.top };
    }

    return bp;
  });
}

/**
 * **Image-aware** — Same keep-together logic as tables: prefer to
 * push a page-spanning image to the next page when it fits there.
 */
function applyImageAwareBreak(
  breaks: BreakPosition[],
  blocks: BlockInfo[],
  pageH: number,
): BreakPosition[] {
  return breaks.map((bp) => {
    const idx = bp.blockIndex;
    if (idx <= 0) return bp;

    const prev = blocks[idx - 1];
    const prevH = prev.bottom - prev.top;
    if (prev.nodeType === "image" && prevH <= pageH) {
      return { blockIndex: idx - 1, pageTop: prev.top };
    }

    return bp;
  });
}

/**
 * **Orphan avoidance** — If the first block on a new page is very
 * short (e.g. a one-liner), pull the break one block earlier so
 * it isn't stranded alone.
 */
function applyAvoidOrphanBlock(
  breaks: BreakPosition[],
  blocks: BlockInfo[],
): BreakPosition[] {
  return breaks.map((bp) => {
    const idx = bp.blockIndex;
    if (idx <= 0 || idx >= blocks.length) return bp;

    const orphan = blocks[idx];
    const orphanH = orphan.bottom - orphan.top;
    if (orphanH < ORPHAN_THRESHOLD && orphan.nodeType === "paragraph") {
      const prev = idx - 1;
      if (prev >= 0 && blocks[prev].nodeType !== "heading") {
        return { blockIndex: prev, pageTop: blocks[prev].top };
      }
    }

    return bp;
  });
}

/**
 * **Widow avoidance** — If the last block before a break is very
 * short, pull the break earlier so it doesn't sit alone at the
 * bottom of the page.
 */
function applyAvoidWidowBlock(
  breaks: BreakPosition[],
  blocks: BlockInfo[],
  _pageH: number,
): BreakPosition[] {
  return breaks.map((bp) => {
    const idx = bp.blockIndex;
    if (idx <= 1) return bp;

    const widow = blocks[idx - 1];
    const widowH = widow.bottom - widow.top;
    if (widowH < WIDOW_THRESHOLD && widow.nodeType === "paragraph") {
      const prev = idx - 2;
      if (prev >= 0 && blocks[prev].nodeType !== "heading") {
        return { blockIndex: idx - 1, pageTop: widow.top };
      }
    }

    return bp;
  });
}

/**
 * Remove duplicate break positions that may result from multiple
 * semantic rules converging on the same block index.
 */
function deduplicateBreaks(breaks: BreakPosition[]): BreakPosition[] {
  const seen = new Set<number>();
  const result: BreakPosition[] = [];
  for (const bp of breaks) {
    if (!seen.has(bp.blockIndex)) {
      seen.add(bp.blockIndex);
      result.push(bp);
    }
  }
  return result;
}
